import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, onlyDigits, RENDA_FAIXA1_LIMITE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CheckCircle2, Loader2, Lock, CalendarClock } from "lucide-react";
import Header from "@/components/Header";
import { StepResponsavel, StepConjuge, StepFamilia, StepEndereco, StepEleitoral, StepSocial, StepPNE, StepIDOSO, StepRenda, StepDeclaracao } from "./inscricao/Steps";
import { validateStep } from "./inscricao/validators";

const STEPS = ["Responsável", "Cônjuge", "Família", "Endereço", "Eleitoral", "Social", "PNE", "Idoso", "Renda", "Declaração"];

function stepClass(index, current) {
  if (index === current) return "step-active";
  if (index < current) return "step-done";
  return "step-pending";
}

const initialForm = {
  nome_completo: "", cpf: "", rg: "", cnh: "", data_nascimento: "", sexo: "",
  estado_civil: "", telefone: "", whatsapp: "", email: "",
  conjuge_nome: "", conjuge_cpf: "", conjuge_rg: "", conjuge_data_nascimento: "",
  qtd_moradores: 1, qtd_dependentes: 0, qtd_criancas: 0, qtd_idosos: 0, qtd_pcd: 0,
  grau_escolaridade: "", ocupacao: "",
  cep: "", rua: "", numero: "", bairro: "", complemento: "", cidade: "Grajaú", estado: "MA",
  tipo_moradia: "", area_de_risco: false,
  titulo_eleitor: "", zona_eleitoral: "", secao_eleitoral: "",
  possui_cadunico: false, nis: "", beneficiario_programa: false, qual_programa: "",
  recebe_bolsa_familia: false, possui_pcd: false, possui_idoso: false,
  descricao_pcd: "", tipo_deficiencia: "", cid_pne: "", pne_e_responsavel: false,
  idade_idoso: "", descricao_idoso: "", idoso_e_responsavel: false,
  renda_responsavel: 0, renda_conjuge: 0, outras_rendas: 0,
  declaracao_veracidade: false,
};

async function fetchCep(cep) {
  try {
    const r = await fetch(`https://viacep.com.br/ws/${cep}/json/`).then((x) => x.json());
    return r.erro ? null : r;
  } catch (e) {
    logger.warn("Falha ao buscar CEP", e);
    return null;
  }
}

function buildPayload(form) {
  return {
    ...form,
    cpf: onlyDigits(form.cpf),
    conjuge_cpf: onlyDigits(form.conjuge_cpf),
    cep: onlyDigits(form.cep),
    telefone: onlyDigits(form.telefone),
    whatsapp: onlyDigits(form.whatsapp),
  };
}

function StepIndicator({ step }) {
  return (
    <div className="mb-6">
      <Progress value={((step + 1) / STEPS.length) * 100} className="h-2" />
      <div className="mt-3 flex flex-wrap gap-2 text-xs">
        {STEPS.map((label, i) => (
          <div key={label} className={`px-2.5 py-1 rounded-md font-semibold ${stepClass(i, step)}`}>
            {i + 1}. {label}
          </div>
        ))}
      </div>
    </div>
  );
}

function FooterNav({ step, loading, canSubmit, onPrev, onNext, onSubmit }) {
  const isLast = step === STEPS.length - 1;
  return (
    <div className="flex justify-between mt-6">
      <Button variant="outline" onClick={onPrev} disabled={step === 0} data-testid="btn-prev">
        <ChevronLeft className="w-4 h-4 mr-1" />Anterior
      </Button>
      {!isLast ? (
        <Button onClick={onNext} className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-next">
          Próximo<ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      ) : (
        <Button onClick={onSubmit} disabled={loading || !canSubmit} className="bg-[#00883A] hover:bg-[#007030]" data-testid="btn-finalizar">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          Finalizar Inscrição
        </Button>
      )}
    </div>
  );
}

function StepRenderer({ step, form, update, isCasado, rendaTotal, buscarCEP }) {
  switch (step) {
    case 0: return <StepResponsavel form={form} update={update} />;
    case 1: return <StepConjuge form={form} update={update} isCasado={isCasado} />;
    case 2: return <StepFamilia form={form} update={update} />;
    case 3: return <StepEndereco form={form} update={update} onCepBlur={buscarCEP} />;
    case 4: return <StepEleitoral form={form} update={update} />;
    case 5: return <StepSocial form={form} update={update} />;
    case 6: return <StepPNE form={form} update={update} />;
    case 7: return <StepIDOSO form={form} update={update} />;
    case 8: return <StepRenda form={form} update={update} rendaTotal={rendaTotal} />;
    case 9: return <StepDeclaracao form={form} update={update} rendaTotal={rendaTotal} />;
    default: return null;
  }
}

export default function InscricaoPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdminOrFuncionario = user && (user.role === "super_admin" || user.role === "funcionario");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [periodoStatus, setPeriodoStatus] = useState(null);
  const [periodoInfo, setPeriodoInfo] = useState(null);

  useEffect(() => {
    // Só bloqueia inscrições públicas fora do período. Admin/funcionário sempre passa.
    if (isAdminOrFuncionario) return;
    api.get("/config/periodo-inscricoes")
      .then((r) => { setPeriodoStatus(r.data.status); setPeriodoInfo(r.data); })
      .catch((e) => logger.warn("Falha ao verificar período", e));
  }, [isAdminOrFuncionario]);

  const bloqueado = !isAdminOrFuncionario && periodoStatus && !periodoStatus.aberto;

  const update = useCallback((k, v) => setForm((p) => ({ ...p, [k]: v })), []);

  const rendaTotal = useMemo(
    () => Number(form.renda_responsavel || 0) + Number(form.renda_conjuge || 0) + Number(form.outras_rendas || 0),
    [form.renda_responsavel, form.renda_conjuge, form.outras_rendas]
  );

  const isCasado = form.estado_civil === "Casado" || form.estado_civil === "União Estável";

  const buscarCEP = useCallback(async (cep) => {
    const d = onlyDigits(cep);
    if (d.length !== 8) return;
    const r = await fetchCep(d);
    if (r) setForm((p) => ({ ...p, rua: r.logradouro || p.rua, bairro: r.bairro || p.bairro, cidade: r.localidade || p.cidade, estado: r.uf || p.estado }));
  }, []);

  const next = () => {
    const err = validateStep(step, form, isCasado);
    if (err) { toast.error(err); return; }
    if (step < STEPS.length - 1) setStep(step + 1);
  };
  const prev = () => step > 0 && setStep(step - 1);

  const submit = async () => {
    const err = validateStep(step, form, isCasado);
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
      const r = await api.post("/inscricoes", buildPayload(form));
      toast.success("Inscrição realizada com sucesso!");
      nav(`/sucesso/${r.data.protocolo}`, { state: { inscricao: r.data } });
    } catch (e) {
      logger.error("Falha ao enviar inscrição", e);
      toast.error(e.response?.data?.detail || "Erro ao enviar inscrição");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="inscricao-page">
        <div className="mb-6">
          <h1 className="text-3xl font-black text-[#083CA6]">Pré-Inscrição MCMV 2026</h1>
          <p className="text-sm text-slate-600 mt-1">
            Preencha os dados em {STEPS.length} etapas. Renda familiar máxima: até R$ {RENDA_FAIXA1_LIMITE.toLocaleString("pt-BR")},00 brutos mensais (Faixa 1).
          </p>
        </div>

        {bloqueado ? (
          <Card className="border-yellow-300 bg-yellow-50" data-testid="periodo-bloqueado-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-yellow-800">
                <Lock className="w-5 h-5" /> Inscrições {periodoStatus?.motivo === "encerrado" ? "Encerradas" : "Ainda Não Abertas"}
              </CardTitle>
              <CardDescription>
                {(() => {
                  if (periodoStatus?.motivo === "encerrado")
                    return "O período de pré-inscrições foi encerrado.";
                  if (periodoStatus?.motivo === "nao_iniciado")
                    return "O período de pré-inscrições ainda não começou.";
                  return "As inscrições estão temporariamente desativadas.";
                })()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert className="bg-white border-yellow-400">
                <CalendarClock className="w-4 h-4 text-yellow-700" />
                <AlertTitle>Período de Inscrições</AlertTitle>
                <AlertDescription className="text-sm space-y-1 mt-1">
                  {periodoInfo?.inicio && (
                    <div><strong>Início:</strong> {new Date(periodoInfo.inicio).toLocaleString("pt-BR")}</div>
                  )}
                  {periodoInfo?.fim && (
                    <div><strong>Fim:</strong> {new Date(periodoInfo.fim).toLocaleString("pt-BR")}</div>
                  )}
                  <div className="pt-2 text-slate-600">
                    Fique atento aos comunicados oficiais da Prefeitura Municipal de Grajaú-MA.
                  </div>
                </AlertDescription>
              </Alert>
              <div className="mt-4">
                <Button onClick={() => nav("/")} variant="outline" data-testid="btn-voltar-home">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Voltar para a Home
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <StepIndicator step={step} />

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>{step + 1}. {STEPS[step]}</CardTitle>
                <CardDescription>Etapa {step + 1} de {STEPS.length}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <StepRenderer step={step} form={form} update={update} isCasado={isCasado} rendaTotal={rendaTotal} buscarCEP={buscarCEP} />
              </CardContent>
            </Card>

            <FooterNav step={step} loading={loading} canSubmit={form.declaracao_veracidade} onPrev={prev} onNext={next} onSubmit={submit} />
          </>
        )}
      </div>
    </div>
  );
}
